import React from "react";
import styled from "styled-components";

import CustomTooltip from "@common/styles/components/tooltip/CustomTooltip";

/**
 * IconButton - A reusable, accessible, and performant icon button with tooltip support.
 *
 * Props:
 * - tooltip: string (tooltip text)
 * - placement: string (tooltip placement)
 * - children: node (icon or content)
 * - onClick: function (event handler)
 * - id: string
 * - name: string
 * - className: string
 * - disabled: boolean
 * - isDisabledTooltip: boolean
 * - classNameTooltip: string
 * - type: string (button type, defaults to 'button')
 * - ariaLabel: string (for accessibility)
 * - ...rest: any other props
 */
const StyledButton = styled.button`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  border: none;
  background-color: transparent;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
`;

const IconButton = React.memo(
  React.forwardRef(
    (
      {
        name,
        onClick,
        tooltip,
        children,
        disabled,
        placement,
        ariaLabel,
        className,
        id = "icon-btn",
        type = "button",
        classNameTooltip,
        isDisabledTooltip,
        ...rest
      },
      ref,
    ) => {
      const tooltipMsg = disabled && !isDisabledTooltip ? "" : tooltip;

      const handleClick = event => {
        if (onClick) onClick({ target: { id, name } });
        event.stopPropagation();
      };

      return (
        <CustomTooltip text={tooltipMsg} placement={placement} className={classNameTooltip}>
          <StyledButton
            id={id}
            ref={ref}
            type={type}
            name={name || id}
            disabled={disabled}
            className={className}
            data-tip={tooltipMsg}
            onClick={handleClick}
            aria-label={ariaLabel}
            {...rest}
          >
            {children}
          </StyledButton>
        </CustomTooltip>
      );
    },
  ),
);

export default IconButton;
